/* eslint-disable @nx/enforce-module-boundaries */
/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useRef, useState } from 'react';
import clsx from 'clsx';
import { useApolloClient } from '@apollo/client';
import { CardCarouselContainer, CardLayeredVariations } from '@marriott/mi-ui-library';
import { getWCMModeFlagValue } from '@marriott/shared/mi-helper-utils';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { getCurrentDate, getTomorrowDate } from '@marriott/mi-offers-components';
import { getCtaUrl, useGetBreakpoint } from '@marriott/mi-leisure-components/utils';
import {
  phoenixResortsCarousel,
  PhoenixSearchPropertiesByArea,
  phoenixLeisureSearchPropertiesByLocation,
  phoenixStorefrontDestinationProperties,
  phoenixLeisureSearchPropertiesByGeolocation,
  phoenixLeisureSearchPropertiesByIds,
} from '@marriott/mi-leisure-graphql';

import { isCityDestination } from '../PropertiesList/utils/helper';
import { isValidNumberString, milesToMeters, shouldUseMockData } from '../../utils/CommonUtils';
import {
  getTrackingProperties,
  generateOffsetNumber,
  shuffleResorts,
  mapResortItem,
  getDisplayImage,
} from './utils/helper';

import {
  IMAGE_ALT_TEXT,
  IMPRESSION_TRACK,
  MERCHANDISING_CATEGORY,
  NO_OF_CARDS_TABLET,
  RESORT_CAROUSEL_ERROR_POLICY,
  IMAGE_CACHE_DOMAIN,
} from '../../constants/ResortCardConstants';

import { CardTilesContainer } from './CardTilesContainer';
import { DynamicPropertyCardsProps } from './DynamicPropertyCards.types';
import { StyledContainer } from './DynamicPropertyCards.styles';
import { SkeletonCardCarouselLoader } from './SkeletonCardCarouselLoader';
import { OffersCardsSkeletonLoader } from './OffersCardsSkeletonLoader';

export const DynamicPropertyCardsWrapper: React.FC<DynamicPropertyCardsProps> = props => {
  const { model, acceptLanguage, isAuthorMode, requestId, subDirectoryContextPath } = props;
  const apolloClientRef = useApolloClient(); // create apollo client instance
  const dynamicPropertyCardRef = useRef<HTMLDivElement>(null);
  const [resortResult, setResultResort] = useState<any>([]);
  const [cardCount, setCardCount] = useState(0);
  const [queryComplete, setQueryCompleted] = useState<number>(0);
  const [keys, setKeys] = useState<any>([]);
  const [isMobileViewPort, setIsMobileViewPort] = useState(useGetBreakpoint() === 'mobile');
  const Enable_DTT_queries = props?.isDTT ?? false;
  const [totalResortCount, _setTotalResortCount] = useState({
    totalCount: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [active, setIsActive] = useState(false);
  const acceptLang = acceptLanguage?.split(',')[0];
  const openInaNewTab = model?.openInaNewTab ? JSON.parse(model.openInaNewTab) : false;
  const startDate = getCurrentDate();
  const endDate = getTomorrowDate();
  const {
    latitude = '',
    longitude = '',
    isStateEnabled = false,
    isCountryEnabled = false,
    isPOI = false,
  } = model?.destinationMetaData ?? {};

  const { envVarsObject } = useClientEnvVarsStore();
  const desktopGrid: boolean = model?.desktopVariation === 'grid';
  const isMarshaConfiguration = model?.configuration === 'marshaCodes';
  const imageTag = model?.imageTag || '';
  const cardLayeredVariation: CardLayeredVariations | undefined = model?.cardsType;
  const isWcmModeDisabledFlag = getWCMModeFlagValue();
  let resortKeys = '';
  const [baseRequestVariables, setBaseVariables] = useState<any>({});

  //UseEffect to effectively reduce api calls when authored inside tabcontainer.
  useEffect(() => {
    if (
      (props?.componentPath === 'mi-leisure-components' || props?.componentPath === 'mi-category-components') &&
      !isAuthorMode
    ) {
      const parent =
        dynamicPropertyCardRef.current?.parentElement?.parentElement?.parentElement?.parentElement?.parentElement;
      setIsActive(!parent?.classList.contains('d-none'));
    } else {
      setIsActive(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  //set query request variables
  useEffect(() => {
    if ((isAuthorMode && !isWcmModeDisabledFlag) || !active) return;
    const {
      latitude = '',
      longitude = '',
      stateCode = '',
      countryCode = '',
      isStateEnabled = false,
      isCountryEnabled = false,
      isPOI = false,
    } = model?.destinationMetaData ?? {};
    const filters: any = [];
    if (model?.hotelType) {
      const hotelTypeArray = Array.isArray(model?.hotelType) ? model?.hotelType : [model?.hotelType];
      filters.push({
        type: 'PROPERTY_TYPES',
        dimensions: hotelTypeArray,
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
      resortKeys = resortKeys.concat(hotelTypeArray.join(',')).concat(',');
    }
    if (model?.activities && model?.activities?.length > 0) {
      filters.push({
        type: 'ACTIVITIES',
        dimensions: model?.activities,
      });
      resortKeys = resortKeys.concat(model?.activities).concat(',');
    }
    if (model?.amenities && model?.amenities?.length > 0) {
      filters.push({
        type: 'AMENITIES',
        dimensions: model?.amenities,
      });
      resortKeys = resortKeys.concat(model?.amenities).concat(',');
    }
    if (model?.regions && model?.regions?.length > 0) {
      filters.push({
        type: 'LEISURE_REGIONS',
        dimensions: model?.regions,
      });
      resortKeys = resortKeys.concat(model?.regions);
    }
    if (model?.brands && model?.brands?.length > 0) {
      filters.push({
        type: 'BRANDS',
        dimensions: model?.brands,
      });
      resortKeys = resortKeys.concat(model?.brands);
    }
    if (isCountryEnabled) {
      filters.push({
        type: 'COUNTRIES',
        dimensions: [countryCode],
      });
    }
    if (isStateEnabled) {
      filters.push({
        type: 'STATES',
        dimensions: [stateCode],
      });
    }
    let updatedOffset;
    let offsetNumber = 0;
    if (typeof window !== 'undefined') {
      updatedOffset =
        localStorage?.getItem('filteredResortCount') != null ? localStorage.getItem('filteredResortCount') : '0';
      offsetNumber = updatedOffset != null ? parseInt(updatedOffset) : 0;
    }

    let partialRequestVariables: any = {};
    if (Enable_DTT_queries) {
      if (isPOI || (latitude && longitude && !isStateEnabled && !isCountryEnabled)) {
        partialRequestVariables = {
          limit: parseInt(model?.totalNumberOfCards),
          offset: 0,
          search: {
            latitude: parseFloat(latitude),
            longitude: parseFloat(longitude),
            ...(isCityDestination(model?.destinationMetaData) &&
              model?.destinationMetaData?.radius !== null &&
              isValidNumberString(model?.destinationMetaData.radius) && {
                distance: milesToMeters(model?.destinationMetaData.radius),
              }),
            facets: {
              terms: filters,
            },
          },
          sort: {
            fields: [
              {
                field: 'DISTANCE',
                direction: 'ASC',
              },
            ],
          },
        };
      } else {
        if (Enable_DTT_queries) {
          partialRequestVariables = {
            limit: parseInt(model?.totalNumberOfCards),
            offset: 0,
            search: {
              options: {
                numberInParty: 1,
                numberOfGuestRooms: 1,
              },
              facets: {
                terms: filters,
              },
            },
          };
        } else {
          partialRequestVariables = {
            limit: parseInt(model?.totalNumberOfCards),
            offset: offsetNumber, //offset,
            search: {
              keyword: '*:*',
              facets: {
                terms: filters,
              },
            },
          };
        }
      }
    } else {
      if (isPOI || (latitude && longitude && !isStateEnabled && !isCountryEnabled)) {
        partialRequestVariables = {
          limit: parseInt(model?.totalNumberOfCards),
          offset: 0,
          version: 'V1',
          search: {
            latitude: parseFloat(latitude),
            longitude: parseFloat(longitude),
            options: {
              startDate: startDate,
              endDate: endDate,
              includeUnavailableProperties: true,
            },
            facets: {
              terms: filters,
            },
          },
          sort: {
            fields: [
              {
                field: 'DISTANCE',
                direction: 'ASC',
              },
            ],
          },
        };
      } else {
        if (Enable_DTT_queries) {
          partialRequestVariables = {
            limit: parseInt(model?.totalNumberOfCards),
            offset: 0,
            search: {
              options: {
                numberInParty: 1,
                numberOfGuestRooms: 1,
              },
              facets: {
                terms: filters,
              },
            },
          };
        } else {
          partialRequestVariables = {
            limit: parseInt(model?.totalNumberOfCards),
            offset: offsetNumber, //offset,
            search: {
              keyword: '*:*',
              facets: {
                terms: filters,
              },
            },
          };
        }
      }
    }
    if (queryComplete === 0) {
      partialRequestVariables.offset = offsetNumber;
    }

    const resortObj: any = {};
    resortObj[resortKeys] = totalResortCount.totalCount;

    if (typeof window !== 'undefined' && totalResortCount.totalCount !== 0) {
      const resortObject: any = localStorage?.getItem('resortObject');
      const parsedResortObject: any = JSON.parse(resortObject);
      const newObj = {
        ...parsedResortObject,
        ...resortObj,
      };
      localStorage.setItem('resortObject', JSON.stringify(newObj));
    }

    const marshaAPIRequestVariables: any = {
      search: {
        ids: Array.isArray(model?.marshaCodes) ? model.marshaCodes : [],
      },
      filter: ['HOTEL_MARKETING_CAPTION'],
    };

    /*
      To enable images by tags:
      - `includeImagesByTags`: This flag ensures that image data is fetched only when an image tag is provided in the query.
    */
    if (imageTag?.trim()) {
      partialRequestVariables.includeImagesByTags = true;
      partialRequestVariables.input = {
        limit: 1, //only one image is required.
        tags: [imageTag],
      };
      marshaAPIRequestVariables.includeImagesByTags = true;
      marshaAPIRequestVariables.input = {
        limit: 1, //only one image is required.
        tags: [imageTag],
      };
    }

    setBaseVariables(isMarshaConfiguration ? marshaAPIRequestVariables : partialRequestVariables);
  }, [active]);

  useEffect(() => {
    if (Object.keys(baseRequestVariables)?.length && active) {
      fetchResortsData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [baseRequestVariables]);

  useEffect(() => {
    function handleResize() {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      setIsMobileViewPort(useGetBreakpoint() === 'mobile');
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load mock model dynamically only for dev mode
  const GetMockData = async (isAuthorMode: boolean) => {
    if (shouldUseMockData(isAuthorMode)) {
      const mockModule = await import('./__mock__/DynamicPropertyCardsUXL.mock');
      return isMarshaConfiguration ? mockModule.marshaConfigUXLMock : mockModule.dynamicConfigUXLMock;
    }
    return {};
  };

  useEffect(() => {
    if (isAuthorMode && !isWcmModeDisabledFlag) {
      const fetchMockData = async () => {
        const mockData = await GetMockData(isAuthorMode);
        const mappedUxlData = processUxlData(mockData);
        processleisureCarousel(mappedUxlData);
      };
      fetchMockData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthorMode]);

  const processDataOnComplete = (queryData: any) => {
    setQueryCompleted(1);
    const mappedUxlData = Enable_DTT_queries ? processUxlDataDTT(queryData) : processUxlData(queryData);
    processleisureCarousel(mappedUxlData);
  };
  const handleOnError = () => {
    setIsLoading(false);
  };

  const fetchResortsData = async () => {
    const query = isMarshaConfiguration
      ? phoenixLeisureSearchPropertiesByIds
      : (latitude && longitude && !isStateEnabled && !isCountryEnabled) || isPOI
      ? Enable_DTT_queries
        ? phoenixLeisureSearchPropertiesByGeolocation
        : phoenixStorefrontDestinationProperties
      : isStateEnabled || isCountryEnabled
      ? Enable_DTT_queries
        ? phoenixLeisureSearchPropertiesByLocation
        : PhoenixSearchPropertiesByArea
      : Enable_DTT_queries
      ? phoenixLeisureSearchPropertiesByLocation
      : phoenixResortsCarousel;
    const queryCommonContext = {
      context: {
        headers: {
          'x-request-id': requestId + '-o-leisure-cardcarousal-resorts',
          'accept-language': acceptLang,
          ...(envVarsObject?.['NEXT_PUBLIC_UAT_AUTH_TOKEN'] &&
            isWcmModeDisabledFlag && { Authorization: envVarsObject?.['NEXT_PUBLIC_UAT_AUTH_TOKEN'] }),
        },
      },
    };
    const load = {
      variables: {
        ...baseRequestVariables,
      },
      ...queryCommonContext,
    };
    try {
      const { data, error } = await apolloClientRef.query({
        fetchPolicy: 'network-only',
        errorPolicy: RESORT_CAROUSEL_ERROR_POLICY,
        query: query,
        ...load,
      });
      if (data) {
        processDataOnComplete(data);
      } else if (error) {
        handleOnError();
      }
    } catch (e) {
      //Need this to stop carousel when graphql does not return error or gives 500
      handleOnError();
    }
  };

  // To be removed once the DTT-only flow is implemented.
  const processUxlData = (uxlData: any) => {
    const response = uxlData || [];
    const offsetValue = generateOffsetNumber(model, resortKeys);
    localStorage.setItem('filteredResortCount', JSON.stringify(offsetValue));

    const isPOIContext = isPOI || (latitude && longitude && !isStateEnabled && !isCountryEnabled);

    const filteredResortCount = isPOIContext
      ? response?.searchLowestAvailableRatesByGeolocation?.total
      : response?.searchPropertiesByKeyword?.total ?? response?.searchByLocation?.total ?? 0;

    totalResortCount.totalCount = filteredResortCount;

    const resortsDataMap = isPOIContext
      ? response?.searchLowestAvailableRatesByGeolocation?.edges
      : isMarshaConfiguration
      ? response?.searchPropertiesByIds?.edges
      : response?.searchPropertiesByKeyword?.edges ?? response?.searchByLocation?.edges ?? [];

    const defaultResorts = resortsDataMap
      ?.filter((edge: any) => {
        return isPOIContext
          ? edge?.node?.property?.basicInformation?.bookable === true
          : edge?.node?.basicInformation?.bookable === true;
      })
      ?.map((item: any) => {
        const node = item?.node;
        const mediaNode = isPOIContext ? node?.property?.media : node?.media;
        const basicInfo = isPOIContext ? node?.property?.basicInformation : node?.basicInformation;
        const brandPhotos = basicInfo?.brand?.photos ?? [];

        const image = getDisplayImage(mediaNode, cardLayeredVariation, brandPhotos);
        const ctaUrl = getCtaUrl(node, subDirectoryContextPath);

        return {
          ...mapResortItem(item, cardLayeredVariation, subDirectoryContextPath),
          image,
          ctaUrl,
        };
      });

    return { resortsData: shuffleResorts(defaultResorts) };
  };

  const processUxlDataDTT = (uxlData: any) => {
    const response = uxlData || [];
    const offsetValue = generateOffsetNumber(model, resortKeys);
    localStorage.setItem('filteredResortCount', JSON.stringify(offsetValue));

    const isPOIContext = isPOI || (latitude && longitude && !isStateEnabled && !isCountryEnabled);

    const filteredResortCount = isPOIContext
      ? response?.search?.properties?.searchByGeolocation?.total
      : isStateEnabled || isCountryEnabled
      ? response?.search?.properties?.searchByLocation?.total
      : response?.search?.properties?.searchByLocation?.total ?? 0;

    totalResortCount.totalCount = filteredResortCount;

    const resortsDataMap = isPOIContext
      ? response?.search?.properties?.searchByGeolocation?.edges
      : isStateEnabled || isCountryEnabled
      ? response?.search?.properties?.searchByLocation?.edges
      : isMarshaConfiguration
      ? response?.searchPropertiesByIds?.edges
      : response?.search?.properties?.searchByLocation?.edges ?? [];

    const defaultResorts = resortsDataMap
      ?.filter((edge: any) => edge?.node?.basicInformation?.bookable === true)
      ?.map((item: any) => mapResortItem(item, cardLayeredVariation, subDirectoryContextPath));

    return { resortsData: shuffleResorts(defaultResorts) };
  };

  const processleisureCarousel = (data: any) => {
    const count = data?.resortsData?.length;
    const cardKeys = Object?.keys(data?.resortsData);
    setCardCount(count);
    setKeys(cardKeys);
    const resortMap = data?.resortsData?.map((card: any, index: number) => {
      let imageUri = '';
      let imageUriLg = '';
      if (card?.image) {
        if (card?.image?.includes('/is/image')) {
          imageUri = !isAuthorMode ? `${card?.image}?wid=364&fit=constrain` : `${card?.image}`;
          imageUriLg = !isAuthorMode ? `${card?.image}?wid=800&fit=constrain` : `${card?.image}`;
        } else if (card?.image?.includes('marriottassets') || card?.image?.includes('/content/dam')) {
          imageUri = `${card.image}?downsize=365px:*`;
          imageUriLg = `${card.image}?downsize=800px:*`;
        }
        if (!imageUri.startsWith(IMAGE_CACHE_DOMAIN)) {
          imageUri = IMAGE_CACHE_DOMAIN + imageUri;
          imageUriLg = IMAGE_CACHE_DOMAIN + imageUriLg;
        }
      }
      const cardLocDetails = model?.trackingProperties?.location
        ? `${model.trackingProperties.location}-card-${index}`
        : `card-${index}`;
      const eyeBrowText = card?.country;
      const badgeText =
        card?.newProperty === true
          ? model?.newTagLabel
          : card?.isFullyRenovated === true
          ? model?.fullyRenovatedLabel
          : card?.isRecentlyRenovated === true
          ? model?.recentlyRenovatedLabel
          : card?.isAdultsOnly === true
          ? model?.adultsOnlyLabel
          : card?.hasRenovatedRooms === true
          ? model?.renovatedRoomsLabel
          : null;

      return {
        ':items': {
          [`card-carousel-${index}`]: {
            trackingProperties: {
              atCCeVar48: null,
              additionalTrackingVariables: null,
              trackingDescription: card?.header,
              description: card?.header,
              clickTrack: model?.trackingProperties?.clickTrack ? true : false,
              impressionTrack: false,
              location: cardLocDetails,
              trackingContentPosition: cardLocDetails,
              merchandisingCategory: 'nonCobrand',
              impressionCount: false,
              trackingTag: model?.trackingProperties?.trackingTag,
              trackingResortType: null,
              cardLocation: model?.trackingProperties?.cardLocation,
              leftArrowDesc: model?.trackingProperties?.leftArrowDesc,
              rightArrowDesc: model?.trackingProperties?.rightArrowDesc,
            },
            eyebrow: eyeBrowText,
            styleclass: model?.cardTheme,
            headerTag: 'h3',
            icon: '',
            impressionTrack: IMPRESSION_TRACK,
            cardLayeredVariations: cardLayeredVariation,
            badgeIcon: '',
            imgAltText: card?.altText || IMAGE_ALT_TEXT,
            clickTrack: model?.trackingProperties?.clickTrack ? true : false,
            fileReferenceImage: imageUriLg,
            header: card.header,
            fontSize: 't-subtitle-l',
            ':type': 'mi-aem-common-spa/components/content/cardlayered',
            [`${
              cardLayeredVariation === CardLayeredVariations.Square
                ? 'dynamicMedia'
                : cardLayeredVariation === CardLayeredVariations.Wide
                ? 'dynamicMediaWide'
                : 'dynamicMediaTall'
            }`]: {
              altText: card?.altText || IMAGE_ALT_TEXT,
              assetPath: imageUriLg,
              dynamic: true,
              renditions: [
                {
                  renditionPath: imageUriLg,
                  mediaValue: '768px',
                  width: 0,
                  dynamic: true,
                  damPath: imageUriLg,
                  mediaQuery: 'min-width',
                  height: 0,
                },
                {
                  renditionPath: imageUri,
                  mediaValue: '576px',
                  width: 0,
                  dynamic: true,
                  damPath: imageUri,
                  mediaQuery: 'min-width',
                  height: 0,
                },
                {
                  renditionPath: imageUri,
                  mediaValue: '576px',
                  width: 0,
                  dynamic: true,
                  damPath: imageUri,
                  mediaQuery: 'max-width',
                  height: 0,
                },
              ],
              damPath: card?.image,
            },
            merchandisingCategory: MERCHANDISING_CATEGORY,
            impressionCount: IMPRESSION_TRACK,
            ctaLink: card?.ctaUrl,
            badgeText: badgeText,
            logoIcon: card?.brandDetails?.id,
            logoIconAltText: card?.brandDetails?.name,
            openInNewTab: openInaNewTab,
          },
        },
      };
    });
    setResultResort(resortMap);
    setIsLoading(false);
  };

  const allowedComponents = [
    {
      path: '/jcr:content/root/responsivegrid/mi-aem-common-spa/components/content/cardlayered',
      title: 'Card Layered',
    },
  ];

  return (
    <StyledContainer ref={dynamicPropertyCardRef}>
      <div className={clsx(!active ? 'hideCarousel' : '')}>
        {!isLoading ? (
          <div className={clsx(cardCount > 0 ? '' : 'hideCarousel', 'container cardWrapper')}>
            {resortResult &&
              resortResult?.length > 0 &&
              (isMobileViewPort || !desktopGrid ? (
                <CardCarouselContainer
                  headerText={!isMobileViewPort ? model?.titleText : model?.mobileTitle}
                  subHeaderText={model?.subTitleText}
                  noOfCards={model?.noOfCards}
                  ctaLabel={model?.ctaLabel}
                  ctaLink={model?.ctaLink}
                  ctaType={model?.ctaType}
                  cardCount={desktopGrid ? model?.totalNumberOfCards : cardCount}
                  openInaNewTab={openInaNewTab}
                  totalNumberOfCards={desktopGrid ? keys.slice(0, model?.totalNumberOfCards) : keys}
                  trackingProperties={getTrackingProperties(model)}
                  styleclass={model?.styleclass}
                  cqItems={resortResult}
                  componentId={`${model?.componentId}-${Math.random().toString(36).substring(2, 9)}`} //must be unique per component
                  pagePath={''}
                  itemPath={''}
                  wcmMode={model?.wcmMode}
                  isAuthorMode={false}
                  noOfCardsTablet={NO_OF_CARDS_TABLET}
                  allowedComponents={allowedComponents}
                  variation={model?.variation}
                  isControlType2={(model?.styleclass?.includes('phoenix-luxury') || model?.enableControlType2) && true}
                  previousLabel={model?.previousLabel}
                  nextLabel={model?.nextLabel}
                />
              ) : (
                <CardTilesContainer
                  headerText={!isMobileViewPort ? model?.titleText : model?.mobileTitle}
                  subHeaderText={model?.subTitleText}
                  noOfCards={model?.noOfCards}
                  ctaLabel={model?.ctaLabel}
                  ctaLink={model?.ctaLink}
                  ctaType={model?.ctaType}
                  cardCount={model?.totalNumberOfCards}
                  openInaNewTab={openInaNewTab}
                  totalNumberOfCards={keys.slice(0, model?.totalNumberOfCards)}
                  trackingProperties={getTrackingProperties(model)}
                  styleclass={model?.styleclass}
                  cqItems={resortResult}
                  componentId={model?.componentId}
                  pagePath={''}
                  itemPath={''}
                  wcmMode={model?.wcmMode}
                  isAuthorMode={false}
                  noOfCardsTablet={NO_OF_CARDS_TABLET}
                  allowedComponents={allowedComponents}
                  variation={model?.variation}
                />
              ))}
          </div>
        ) : isMobileViewPort || !desktopGrid ? (
          <div>
            <SkeletonCardCarouselLoader cardType={cardLayeredVariation} />
          </div>
        ) : (
          <div className="container">
            <OffersCardsSkeletonLoader isMobileViewPort={isMobileViewPort} model={model} />
          </div>
        )}
      </div>
    </StyledContainer>
  );
};
export default DynamicPropertyCardsWrapper;
